import { jsx as _jsx } from "react/jsx-runtime";
import { voiceRecordingsService } from '@services/voice-recordings.service';
import { isAppApiError } from '@utils/service.utils';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
const VoiceRecordingsContext = createContext(undefined);
export const VoiceRecordingsProvider = ({ children }) => {
    const [remoteRecordings, setRemoteRecordings] = useState({});
    const [localRecordings, setLocalRecordings] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // Computed: Check if at least one language has both recording types in remote
    const hasValidRecordings = useMemo(() => {
        return Object.values(remoteRecordings).some(langRecordings => langRecordings.consentAudio !== undefined &&
            langRecordings.referenceAudio !== undefined);
    }, [remoteRecordings]);
    // Fetch recordings from API
    const fetchRemoteRecordings = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await voiceRecordingsService.getRecordings();
            const recordingsPromises = data.map(async (item) => {
                const languageKey = item.languageKey;
                const [consentAudio, referenceAudio] = await Promise.all([
                    _fetchAudioRecording(languageKey, 'consentAudio'),
                    _fetchAudioRecording(languageKey, 'referenceAudio'),
                ]);
                return {
                    languageKey,
                    recordings: { consentAudio, referenceAudio },
                };
            });
            const results = await Promise.all(recordingsPromises);
            const transformedRecordings = results.reduce((acc, { languageKey, recordings }) => {
                acc[languageKey] = recordings;
                return acc;
            }, {});
            setRemoteRecordings(transformedRecordings);
        }
        catch (err) {
            let errorMessage = 'Failed to fetch recordings';
            if (isAppApiError(err)) {
                errorMessage = err.message;
            }
            setError(errorMessage);
            setRemoteRecordings({});
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    // Add local recording (newly recorded)
    const addLocalRecording = useCallback(({ languageKey, type, blob }) => {
        const id = `${languageKey}-${type}`;
        const url = URL.createObjectURL(blob);
        const recording = {
            id,
            blob,
            url,
            type,
        };
        setLocalRecordings(prev => ({
            ...prev,
            [languageKey]: {
                ...prev[languageKey],
                [type]: recording,
            },
        }));
    }, []);
    // Clear all local recordings
    const clearAllLocalRecordings = useCallback(() => {
        // Cleanup all blob URLs
        Object.values(localRecordings).forEach(langRecordings => {
            Object.values(langRecordings).forEach(recording => {
                if (recording?.blob && recording.url) {
                    URL.revokeObjectURL(recording.url);
                }
            });
        });
        setLocalRecordings({});
    }, [localRecordings]);
    // Clear all remote recordings
    const clearAllRemoteRecordings = useCallback(() => {
        // Cleanup all blob URLs
        Object.values(remoteRecordings).forEach(langRecordings => {
            Object.values(langRecordings).forEach(recording => {
                if (recording?.blob && recording.url) {
                    URL.revokeObjectURL(recording.url);
                }
            });
        });
        setRemoteRecordings({});
    }, [remoteRecordings]);
    // Get local recording for a specific language and type
    const getLocalRecording = useCallback((languageKey, type) => {
        return localRecordings[languageKey]?.[type];
    }, [localRecordings]);
    // Initialize voice recordings
    const initializeVoiceRecordings = useCallback(() => {
        fetchRemoteRecordings();
    }, [fetchRemoteRecordings]);
    // Helper function to fetch audio and create recording object
    const _fetchAudioRecording = async (languageKey, type) => {
        try {
            const blob = await voiceRecordingsService.getRecordingAudio(languageKey, type);
            return {
                id: `${languageKey}-${type}`,
                blob,
                url: URL.createObjectURL(blob),
                type,
            };
        }
        catch (_) {
            // API failed, return empty blob
            const emptyBlob = new Blob([], { type: 'audio/wav' });
            return {
                id: `${languageKey}-${type}`,
                blob: emptyBlob,
                url: URL.createObjectURL(emptyBlob),
                type,
            };
        }
    };
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearAllLocalRecordings();
            clearAllRemoteRecordings();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const value = {
        // State
        remoteRecordings,
        localRecordings,
        isLoading,
        error,
        // Computed
        hasValidRecordings,
        // Actions - Local recordings
        addLocalRecording,
        clearAllLocalRecordings,
        clearAllRemoteRecordings,
        // Actions - Remote recordings
        fetchRemoteRecordings,
        // Query functions
        getLocalRecording,
        // Initialization
        initializeVoiceRecordings,
    };
    return (_jsx(VoiceRecordingsContext.Provider, { value: value, children: children }));
};
export const useVoiceRecordings = () => {
    const context = useContext(VoiceRecordingsContext);
    if (!context) {
        throw new Error('useVoiceRecordings must be used within VoiceRecordingsProvider');
    }
    return context;
};
